import { type AccordionData } from '@/features/cms/components/CMS/types'
import styled from 'styled-components'
import { Body, Headline } from '@shipt/design-system-typography'
import { Image } from '@/features/shared/elements/Image/NextImage'
import { CMSErrorHandler } from '@/features/cms/components/CMS/CMSErrorHandler'
import { spacing } from '@/features/shared/theme/tokens'
import { renderElement } from '@/features/cms/components/CMS/RichTextRenderer'
import { AccordionGroup, Accordion } from '@shipt/design-system-accordion'
import { GridLayoutContainer } from '@/features/shared/styles/layout'
import { Box, Column, Row, Stack } from '@shipt/design-system-layouts'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import { useCMSTrackingEvents } from '@/features/cms/components/CMS/CMSContext'
import { HeadlineTextEmbeddedSection } from '@/features/cms/components/CMS/components/Accordion/HeadlineTextEmbeddedSection'
import { LinkListSection } from '@/features/cms/components/CMS/components/Accordion/LinkListSection'

/**
 * This component corresponds to the `accordion` content type in the CMS.
 *
 * - **Staging** - https://cms-staging.shipt.com/content-type/pool/accordion
 * - **Production** - https://cms.shipt.com/content-type/pool/accordion
 */
export const AccordionLayout = ({
  content_type_id,
  id,
  data: {
    heading,
    subheading_rich_text,
    image,
    image_placement,
    accordion_sections,
    expand_all = false,
    subheading,
  },
}: AccordionData) => {
  const isAltImagePlacement = image_placement === 'left'
  const imagePlacement = isAltImagePlacement ? 'row-reverse' : 'row'
  const { trackCMSElementClicked } = useCMSTrackingEvents()

  try {
    return (
      <LayoutContainer as="section" {...getCMSWrapperProps({ content_type_id, id })}>
        <Column flexBasis="100%" spacing="lg">
          <Box margin={[0, 0, 'xl']}>
            <Headline as="h2">{heading}</Headline>
          </Box>
          {Boolean(subheading_rich_text || subheading) && (
            <Box paddingBottom="xl">
              {!!subheading_rich_text && (
                <Body as="div" align="left" variant="secondary">
                  {subheading_rich_text.elements.map(renderElement)}
                </Body>
              )}
              {!subheading_rich_text && !!subheading && (
                <Body align="left" variant="secondary">
                  {subheading}
                </Body>
              )}
            </Box>
          )}
          <Stack
            direction={{ base: 'column', md: imagePlacement }}
            spacing={{ base: 'xl', md: 'xxl' }}
          >
            <AccordionContainer>
              <AccordionGroup>
                {accordion_sections.map((section, i) => {
                  const { data, content_type_id: section_content_type_id } = section
                  return (
                    <Accordion
                      key={data.heading}
                      title={data.heading}
                      headerAs="h3"
                      defaultExpanded={expand_all || i === 0}
                      shouldPersistContent
                      onClick={() => trackCMSElementClicked({ content: data.heading })}
                    >
                      <Content>
                        {section_content_type_id === 'headline_text_embedded' && (
                          <HeadlineTextEmbeddedSection data={data} />
                        )}
                        {section_content_type_id === 'link_list_section' && (
                          <LinkListSection data={data} />
                        )}
                      </Content>
                    </Accordion>
                  )
                })}
              </AccordionGroup>
            </AccordionContainer>
            {!!image && (
              <ImageContainer>
                <Row justify="center">
                  <Image
                    {...image}
                    alt={image.alt}
                    style={{ width: 'auto', height: 'auto', borderRadius: 8 }}
                  />
                </Row>
              </ImageContainer>
            )}
          </Stack>
        </Column>
      </LayoutContainer>
    )
  } catch (error) {
    return <CMSErrorHandler error={error} contentTypeId={content_type_id} id={id} />
  }
}

const LayoutContainer = styled(GridLayoutContainer)`
  width: 100%;
  --content-max-width: 1080px;
`
const AccordionContainer = styled.div`
  flex: 1;
`
const Content = styled.div`
  white-space: pre-line;
`
const ImageContainer = styled.div`
  flex: 1;
  margin: ${spacing(0, 'auto', 30)};
`
